import { Renderer } from '@/game/renderer';
import { InputHandler } from '@/game/input';
import { AIController } from '@/game/ai';
import { resolveAttack, updateFacing } from '@/game/physics';
import { TRex } from '@/game/fighters/trex';
import { Spinosaurus } from '@/game/fighters/spinosaurus';
import { statsApi } from '@/game/stats-api';
import { getGameConfig } from '@/game/game-config';
import { logger } from '@/game/logger';
import type { FighterType } from '@/shared/constants';
import type { WinStats } from '@/shared/types';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  MAX_HEALTH,
  ROUND_TIME_SECONDS,
  ROUNDS_TO_WIN,
  COLORS,
} from '@/shared/constants';

type FightPhase = 'intro' | 'fight' | 'round-end' | 'match-end';

/** Bright colors cycled on the SHARE button. */
const SHARE_COLORS = ['#ff0044', '#ff6600', '#ffcc00', '#00ff66', '#00ccff', '#6644ff', '#ff44cc'];

/** Share the game link using Web Share API or clipboard fallback. */
async function shareGame(): Promise<void> {
  const config = getGameConfig();
  const url =
    'https://dinokombat.radiantartshop.com?utm_source=share&utm_medium=referral&utm_campaign=fight_share';
  const text = `I just played ${config.title}! ${config.subtitle}`;

  if (navigator.share) {
    try {
      await navigator.share({ title: config.title, text, url });
      return;
    } catch {
      // User cancelled or share failed — fall through to clipboard
    }
  }

  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
  } catch {
    logger.warn('Clipboard write failed');
  }
}

const SHARE_TEXT = '[ SHARE ]';
const SHARE_FONT = 'bold 20px monospace';
const SHARE_Y = CANVAS_HEIGHT / 2 + 40;
const SHARE_X = CANVAS_WIDTH / 2;
/** Approximate half-width/half-height of the share text hitbox. */
const SHARE_HW = 70;
const SHARE_HH = 16;

export class FightScene {
  private player1: TRex;
  private player2: Spinosaurus;
  private ai: AIController;
  private roundTime: number;
  private round = 1;
  private p1Wins = 0;
  private p2Wins = 0;
  private phase: FightPhase = 'intro';
  private phaseTimer = 0;
  private hitThisAttack = false;
  private hitThisAttackP2 = false;
  private announceText = '';
  private renderer: Renderer | null = null;
  private noAi = false;

  /** Stats snapshot shown on match-end screen. */
  private stats: WinStats | null = null;
  private matchResultRecorded = false;

  /** Frame counter for share button color cycling. */
  private shareColorFrame = 0;
  /** Feedback text shown briefly after sharing. */
  private shareConfirmTimer = 0;

  constructor(
    private input: InputHandler,
    private mobile: boolean = false,
  ) {
    this.player1 = new TRex(200, 'right');
    this.player2 = new Spinosaurus(CANVAS_WIDTH - 200, 'left');
    this.ai = new AIController('easy');
    this.noAi = new URLSearchParams(window.location.search).has('noai');
    this.roundTime = ROUND_TIME_SECONDS;
    this.startIntro();
    logger.info(`Fight scene created (mobile=${mobile})`);
  }

  /** Bind renderer so round transitions can reset fighter displays. */
  setRenderer(renderer: Renderer): void {
    this.renderer = renderer;
  }

  private startIntro(): void {
    this.phase = 'intro';
    this.phaseTimer = 120;
    this.announceText = `ROUND ${this.round}`;
  }

  update(): void {
    switch (this.phase) {
      case 'intro':
        this.updateIntro();
        break;
      case 'fight':
        this.updateFight();
        break;
      case 'round-end':
        this.updateRoundEnd();
        break;
      case 'match-end':
        this.updateMatchEnd();
        break;
    }
  }

  private updateIntro(): void {
    this.phaseTimer--;
    if (this.phaseTimer === 60) {
      this.announceText = 'FIGHT!';
    }
    if (this.phaseTimer <= 0) {
      this.phase = 'fight';
    }
  }

  private updateFight(): void {
    this.roundTime -= 1 / 60;

    const p1Input = this.input.getState();
    const attacks = this.input.consumeAttacks();
    p1Input.attack1 = attacks.attack1;
    p1Input.attack2 = attacks.attack2;
    p1Input.special = attacks.special;
    this.player1.applyInput(p1Input);

    const aiInput = this.noAi
      ? {
          left: false,
          right: false,
          up: false,
          down: false,
          attack1: false,
          attack2: false,
          special: false,
          block: false,
        }
      : this.ai.update(this.player2, this.player1);
    this.player2.applyInput(aiInput);

    this.player1.update();
    this.player2.update();

    updateFacing(this.player1, this.player2);

    if (this.player1.isAttacking) {
      if (!this.hitThisAttack) {
        const hit = resolveAttack(this.player1, this.player2);
        if (hit) {
          this.hitThisAttack = true;
          this.renderer?.hitEffects.spawn(hit.x, hit.y, hit.attackType);
        }
      }
    } else {
      this.hitThisAttack = false;
    }

    if (this.player2.isAttacking) {
      if (!this.hitThisAttackP2) {
        const hit = resolveAttack(this.player2, this.player1);
        if (hit) {
          this.hitThisAttackP2 = true;
          this.renderer?.hitEffects.spawn(hit.x, hit.y, hit.attackType);
        }
      }
    } else {
      this.hitThisAttackP2 = false;
    }

    // Update hit effects and apply screen shake
    if (this.renderer) {
      const shake = this.renderer.hitEffects.update();
      this.renderer.applyScreenShake(shake.shakeX, shake.shakeY);
    }

    if (this.player1.health <= 0 || this.player2.health <= 0 || this.roundTime <= 0) {
      this.endRound();
    }
  }

  private endRound(): void {
    const config = getGameConfig();

    if (this.player1.health > this.player2.health) {
      this.p1Wins++;
      this.announceText = `${config.p1Name} WINS THE ROUND!`;
    } else if (this.player2.health > this.player1.health) {
      this.p2Wins++;
      this.announceText = `${config.p2Name} WINS THE ROUND!`;
    } else {
      this.announceText = 'DRAW!';
    }

    if (this.p1Wins >= ROUNDS_TO_WIN || this.p2Wins >= ROUNDS_TO_WIN) {
      this.phase = 'match-end';
      this.phaseTimer = 180;

      if (this.p1Wins >= ROUNDS_TO_WIN) {
        this.announceText = `${config.p1Name} WINS!`;
        this.recordMatchResult(config.p1StatsKey);
      } else {
        this.announceText = `${config.p2Name} WINS!`;
        this.recordMatchResult(config.p2StatsKey);
      }
    } else {
      this.phase = 'round-end';
      this.phaseTimer = 120;
    }
  }

  private updateRoundEnd(): void {
    this.phaseTimer--;

    // Let hit effects finish their animation
    if (this.renderer) {
      const shake = this.renderer.hitEffects.update();
      this.renderer.applyScreenShake(shake.shakeX, shake.shakeY);
    }

    // Check for KO crumble particle spawning
    this.checkKOCrumble();

    if (this.phaseTimer <= 0) {
      this.round++;
      this.player1.reset(200, 'right');
      this.player2.reset(CANVAS_WIDTH - 200, 'left');
      this.roundTime = ROUND_TIME_SECONDS;

      // Reset fighter display transforms so KO animations don't persist
      this.renderer?.resetFighterDisplays('fight-');
      // Clear any lingering hit effects and screen shake
      this.renderer?.hitEffects.clear();
      this.renderer?.applyScreenShake(0, 0);

      logger.info(`Starting round ${this.round}`);
      this.startIntro();
    }
  }

  private updateMatchEnd(): void {
    this.phaseTimer--;

    // Let hit effects finish their animation
    if (this.renderer) {
      const shake = this.renderer.hitEffects.update();
      this.renderer.applyScreenShake(shake.shakeX, shake.shakeY);
    }

    // Check for KO crumble particle spawning
    this.checkKOCrumble();
  }

  /**
   * Check both fighters for KO crumble state and spawn crumble particles.
   */
  private checkKOCrumble(): void {
    if (!this.renderer) return;

    if (this.player1.state === 'ko') {
      this.renderer.checkAndSpawnCrumble('fight-p1', this.player1.x, this.player1.y);
    }
    if (this.player2.state === 'ko') {
      this.renderer.checkAndSpawnCrumble('fight-p2', this.player2.x, this.player2.y);
    }
  }

  /** Record the match winner to the stats API (once per match). */
  private recordMatchResult(winner: FighterType): void {
    if (this.matchResultRecorded) return;
    this.matchResultRecorded = true;
    statsApi.recordWin(winner).then((s) => {
      this.stats = s;
    });
  }

  get isMatchOver(): boolean {
    return this.phase === 'match-end' && this.phaseTimer <= 0;
  }

  /**
   * Handle a click/tap at canvas-space coordinates.
   * Returns true if the click was consumed (e.g. share button hit).
   */
  handleClick(cx: number, cy: number): boolean {
    if (!this.isMatchOver) return false;

    if (
      cx >= SHARE_X - SHARE_HW &&
      cx <= SHARE_X + SHARE_HW &&
      cy >= SHARE_Y - SHARE_HH &&
      cy <= SHARE_Y + SHARE_HH
    ) {
      shareGame().then(() => {
        this.shareConfirmTimer = 120;
      });
      return true;
    }
    return false;
  }

  draw(renderer: Renderer): void {
    const config = getGameConfig();

    // Background and ground are handled by the PixiJS renderer layers

    renderer.drawHealthBar(20, 20, CANVAS_WIDTH / 2 - 60, this.player1.health, MAX_HEALTH);
    renderer.drawHealthBar(
      CANVAS_WIDTH / 2 + 40,
      20,
      CANVAS_WIDTH / 2 - 60,
      this.player2.health,
      MAX_HEALTH,
    );

    renderer.drawText(config.p1Name, 120, 60, { font: '16px monospace', color: config.p1Color });
    renderer.drawText(config.p2Name, CANVAS_WIDTH - 120, 60, {
      font: '16px monospace',
      color: config.p2Color,
    });

    renderer.drawTimer(Math.max(0, this.roundTime));

    renderer.drawText(`Round ${this.round}`, CANVAS_WIDTH / 2, 75, {
      font: '14px monospace',
      color: '#888888',
    });
    renderer.drawText(`${this.p1Wins} - ${this.p2Wins}`, CANVAS_WIDTH / 2, 92, {
      font: '14px monospace',
      color: COLORS.subtitle,
    });

    // Draw fighters with full state info for PixiJS animation
    renderer.drawFighter(
      this.player1.x,
      this.player1.y,
      this.player1.width,
      this.player1.height,
      this.player1.color,
      this.player1.facing,
      '',
      this.player1.state,
      this.player1.attackTimer,
      this.player1.currentAttack,
      'fight-p1',
      'trex',
    );
    renderer.drawFighter(
      this.player2.x,
      this.player2.y,
      this.player2.width,
      this.player2.height,
      this.player2.color,
      this.player2.facing,
      '',
      this.player2.state,
      this.player2.attackTimer,
      this.player2.currentAttack,
      'fight-p2',
      'spinosaurus',
    );

    // Draw hit effect particles
    renderer.hitEffects.draw();

    if (this.phase === 'intro' || this.phase === 'round-end' || this.phase === 'match-end') {
      renderer.drawText(this.announceText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, {
        font: 'bold 48px monospace',
        color: COLORS.title,
      });
    }

    if (this.isMatchOver) {
      const endText = this.mobile ? 'TAP FOR TITLE SCREEN' : 'PRESS ENTER FOR TITLE SCREEN';
      renderer.drawText(endText, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 70, {
        font: '18px monospace',
        color: COLORS.text,
      });

      // Color-cycling SHARE button
      this.shareColorFrame++;
      const colorIdx = Math.floor(this.shareColorFrame / 8) % SHARE_COLORS.length;
      const shareColor = SHARE_COLORS[colorIdx];
      renderer.drawText(SHARE_TEXT, SHARE_X, SHARE_Y, {
        font: SHARE_FONT,
        color: shareColor,
      });

      // Brief confirmation after sharing
      if (this.shareConfirmTimer > 0) {
        this.shareConfirmTimer--;
        renderer.drawText('Link copied!', SHARE_X, SHARE_Y + 24, {
          font: '12px monospace',
          color: '#888888',
        });
      }

      // Show global win stats below the prompt
      this.drawStats(renderer);
    }
  }

  private drawStats(renderer: Renderer): void {
    if (!this.stats) return;

    const config = getGameConfig();
    const x = CANVAS_WIDTH / 2;
    const baseY = CANVAS_HEIGHT / 2 + 110;

    renderer.drawText('ALL-TIME WINS', x, baseY, {
      font: 'bold 14px monospace',
      color: COLORS.subtitle,
    });

    const p1Key = config.p1StatsKey;
    const p2Key = config.p2StatsKey;
    const entries: [string, number, string][] = [
      [config.p1Name, this.stats[p1Key], config.p1Color],
      [config.p2Name, this.stats[p2Key], config.p2Color],
    ];
    entries.sort((a, b) => b[1] - a[1]);

    entries.forEach(([name, wins, color], i) => {
      const y = baseY + 22 + i * 20;
      renderer.drawText(`${name}  ${wins}`, x, y, {
        font: '14px monospace',
        color,
      });
    });
  }
}
